/**
 * useChatRenderer — loads the SAME markdown/action-chip renderer the
 * storefront chatbot widget uses, served by the backend at
 * `/public/plugins/chat-renderer.js`, so the admin transcript shows a
 * message exactly as the visitor saw it (links, call/directions chips,
 * product cards, quick replies).
 *
 * One source of truth lives in the backend
 * (`aapastech-backend/src/lib/ai/chat-renderer-source.ts`); this hook
 * deliberately does not duplicate it. Until the script arrives (or if it
 * fails to load) callers fall back to plain text.
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

export interface ChatRenderResult {
  html: string;
  quickReplies: string[];
}

export interface ChatRenderer {
  render(
    text: string,
    opts?: { streaming?: boolean; cards?: boolean; quickReplies?: boolean }
  ): ChatRenderResult;
  css: string;
  version: string;
}

declare global {
  interface Window {
    TyashinChatRenderer?: ChatRenderer;
  }
}

const STYLE_ID = 'tyashin-chat-renderer-css';
let pending: Promise<ChatRenderer | null> | null = null;

function injectCss(r: ChatRenderer) {
  if (document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent = r.css;
  document.head.appendChild(st);
}

function load(): Promise<ChatRenderer | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.TyashinChatRenderer) {
    injectCss(window.TyashinChatRenderer);
    return Promise.resolve(window.TyashinChatRenderer);
  }
  if (pending) return pending;
  pending = new Promise((resolve) => {
    const base = (api.defaults.baseURL || '/api/v1').replace(/\/$/, '');
    const s = document.createElement('script');
    s.src = `${base}/public/plugins/chat-renderer.js?v=1`;
    s.async = true;
    s.onload = () => {
      const r = window.TyashinChatRenderer ?? null;
      if (r) injectCss(r);
      resolve(r);
    };
    s.onerror = () => {
      pending = null; // allow a retry on the next mount
      resolve(null);
    };
    document.head.appendChild(s);
  });
  return pending;
}

export function useChatRenderer(): ChatRenderer | null {
  const [renderer, setRenderer] = useState<ChatRenderer | null>(() =>
    typeof window !== 'undefined' ? (window.TyashinChatRenderer ?? null) : null
  );

  useEffect(() => {
    if (renderer) return;
    let alive = true;
    load().then((r) => {
      if (alive && r) setRenderer(r);
    });
    return () => {
      alive = false;
    };
  }, [renderer]);

  return renderer;
}
